import React, { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SilaeFormData } from "@/types/silae";
import { cumulTypes } from "@/types/silae";
import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";
import { SilaeDatePicker } from "./SilaeDatePicker";
import { CUMUL_BULLETINS_PERIODS } from "@/constants/code-generation";

interface CumulBulletinsConfigProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix: string; // Ex: "ifBranch.bonuses.0.bases.0" (source) ou "conditions.0" (condition)
}

/**
 * CumulBulletinsConfig - Composant unifié pour sources ET conditions
 *
 * Ce composant détecte automatiquement le contexte (source ou condition) en vérifiant
 * les champs disponibles dans Formik et utilise les noms de champs appropriés :
 *
 * Pour les SOURCES (SilaeSourceFormData):
 * - cumulPeriod, cumulType, cumulCustomStartDate, cumulCustomEndDate, cumulDivisor
 *
 * Pour les CONDITIONS (SilaeCondition):
 * - cumulBulletinsPeriod, cumulBulletinsType, cumulBulletinsDateDebut, cumulBulletinsDateFin, cumulBulletinsDivisor
 *
 * Utilise uniquement:
 * - formik: Pour accéder et mettre à jour les valeurs
 * - fieldPrefix: Chemin complet vers l'objet (source ou condition)
 */
export const CumulBulletinsConfig: React.FC<CumulBulletinsConfigProps> = ({
  formik,
  fieldPrefix,
}) => {
  const { t } = useTranslation();

  // Détection automatique du contexte : vérifier si on a les champs "cumulBulletins*" (conditions)
  // ou les champs "cumul*" sans le préfixe "Bulletins" (sources)
  const hasConditionFields =
    getIn(formik.values, `${fieldPrefix}.cumulBulletinsPeriod`) !== undefined ||
    getIn(formik.values, `${fieldPrefix}.cumulBulletinsType`) !== undefined ||
    getIn(formik.values, `${fieldPrefix}.cumulBulletinsDateDebut`) !==
      undefined;

  const hasSourceFields =
    getIn(formik.values, `${fieldPrefix}.cumulPeriod`) !== undefined ||
    getIn(formik.values, `${fieldPrefix}.cumulType`) !== undefined ||
    getIn(formik.values, `${fieldPrefix}.cumulCustomStartDate`) !== undefined;

  // Si les deux sont undefined, on est probablement dans un wrapper qui réécrit les valeurs
  // Dans ce cas, on détecte via le fieldPrefix lui-même
  const isConditionContext =
    hasConditionFields ||
    (!hasConditionFields &&
      !hasSourceFields &&
      fieldPrefix.includes("conditions"));

  // Noms de champs dynamiques selon le contexte
  const fieldNames = isConditionContext
    ? {
        period: "cumulBulletinsPeriod",
        type: "cumulBulletinsType",
        dateStart: "cumulBulletinsDateDebut",
        dateEnd: "cumulBulletinsDateFin",
        divisor: "cumulBulletinsDivisor",
      }
    : {
        period: "cumulPeriod",
        type: "cumulType",
        dateStart: "cumulCustomStartDate",
        dateEnd: "cumulCustomEndDate",
        divisor: "cumulDivisor",
      };

  // Accéder aux valeurs via getIn avec les noms de champs dynamiques
  const cumulPeriod = getIn(
    formik.values,
    `${fieldPrefix}.${fieldNames.period}`,
  ) as string | undefined;
  const cumulType = getIn(
    formik.values,
    `${fieldPrefix}.${fieldNames.type}`,
  ) as string | undefined;
  const cumulCustomStartDate = getIn(
    formik.values,
    `${fieldPrefix}.${fieldNames.dateStart}`,
  ) as string | undefined;
  const cumulCustomEndDate = getIn(
    formik.values,
    `${fieldPrefix}.${fieldNames.dateEnd}`,
  ) as string | undefined;
  const cumulDivisor = getIn(
    formik.values,
    `${fieldPrefix}.${fieldNames.divisor}`,
  ) as string | undefined;

  /**
   * Gère le changement de période avec mise à jour automatique des dates
   */
  const handlePeriodChange = (periodKey: string) => {
    formik.setFieldValue(`${fieldPrefix}.${fieldNames.period}`, periodKey);

    if (periodKey !== "custom") {
      const period =
        CUMUL_BULLETINS_PERIODS[
          periodKey as keyof typeof CUMUL_BULLETINS_PERIODS
        ];
      if (period) {
        formik.setFieldValue(
          `${fieldPrefix}.${fieldNames.dateStart}`,
          period.debut,
        );
        formik.setFieldValue(
          `${fieldPrefix}.${fieldNames.dateEnd}`,
          period.fin,
        );
      }
    } else {
      // Réinitialiser les dates si on passe en mode personnalisé
      formik.setFieldValue(`${fieldPrefix}.${fieldNames.dateStart}`, "");
      formik.setFieldValue(`${fieldPrefix}.${fieldNames.dateEnd}`, "");
    }
  };

  // Initialisation des dates au montage si nécessaire
  useEffect(() => {
    const currentPeriod = cumulPeriod || "year_current";

    // Initialiser uniquement si les deux dates sont vides et la période n'est pas "custom"
    if (
      !cumulCustomStartDate &&
      !cumulCustomEndDate &&
      currentPeriod !== "custom"
    ) {
      const period =
        CUMUL_BULLETINS_PERIODS[
          currentPeriod as keyof typeof CUMUL_BULLETINS_PERIODS
        ];
      if (period && period.debut && period.fin) {
        formik.setFieldValue(
          `${fieldPrefix}.${fieldNames.dateStart}`,
          period.debut,
        );
        formik.setFieldValue(
          `${fieldPrefix}.${fieldNames.dateEnd}`,
          period.fin,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Exécuter uniquement au montage

  const currentPeriod = cumulPeriod || "year_current";
  const isCustomPeriod = currentPeriod === "custom";

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/20">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
          {t("cumulBulletinsConfig.title")}
        </span>
      </div>

      {/* Période de cumul */}
      <div className="space-y-2">
        <Label>{t("cumulBulletinsConfig.period.label")}</Label>
        <Select value={currentPeriod} onValueChange={handlePeriodChange}>
          <SelectTrigger>
            <SelectValue
              placeholder={t("cumulBulletinsConfig.period.placeholder")}
            />
          </SelectTrigger>
          <SelectContent>
            {Object.values(CUMUL_BULLETINS_PERIODS).map((period) => (
              <SelectItem key={period.key} value={period.key}>
                {period.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Dates personnalisées */}
      {isCustomPeriod && (
        <div className="flex flex-wrap gap-4">
          <div className="space-y-2 flex-1">
            <SilaeDatePicker
              value={cumulCustomStartDate || ""}
              onChange={(value) => {
                formik.setFieldValue(
                  `${fieldPrefix}.${fieldNames.dateStart}`,
                  value,
                );
              }}
              label={t("cumulBulletinsConfig.customDates.startDate.label")}
              allowSilaeDateVariables={true}
            />
          </div>
          <div className="space-y-2 flex-1">
            <SilaeDatePicker
              value={cumulCustomEndDate || ""}
              onChange={(value) => {
                formik.setFieldValue(
                  `${fieldPrefix}.${fieldNames.dateEnd}`,
                  value,
                );
              }}
              label={t("cumulBulletinsConfig.customDates.endDate.label")}
              allowSilaeDateVariables={true}
            />
          </div>
        </div>
      )}

      {/* Type de cumul */}
      <div className="space-y-2">
        <Label>{t("cumulBulletinsConfig.type.label")}</Label>
        <Select
          value={cumulType || ""}
          onValueChange={(value) => {
            formik.setFieldValue(`${fieldPrefix}.${fieldNames.type}`, value);
          }}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={t("cumulBulletinsConfig.type.placeholder")}
            />
          </SelectTrigger>
          <SelectContent className="max-h-64 overflow-y-auto">
            {Object.entries(cumulTypes).map(([key, value]) => (
              <SelectItem key={key} value={key}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Diviseur optionnel */}
      <div className="space-y-2">
        <Label>{t("cumulBulletinsConfig.divisor.label")}</Label>
        <Input
          type="number"
          placeholder={t("cumulBulletinsConfig.divisor.placeholder")}
          value={cumulDivisor || ""}
          onChange={(e) => {
            formik.setFieldValue(
              `${fieldPrefix}.${fieldNames.divisor}`,
              e.target.value,
            );
          }}
          onBlur={() => {
            formik.setFieldTouched(
              `${fieldPrefix}.${fieldNames.divisor}`,
              true,
            );
          }}
        />
        <p className="text-xs text-blue-600 dark:text-blue-400">
          💡 {t("cumulBulletinsConfig.divisor.helpText")}
        </p>
      </div>

    </div>
  );
};

export default CumulBulletinsConfig;
